<script setup lang="ts">
import { computed } from 'vue'

import type { InsightConfigBrowserFilters } from '../../../composables/useInsightConfigBrowser'
import type { InsightConfig } from '../../../models/insightConfig.types'
import type { WorkbenchAsyncState } from '../../../models/workbench.types'
import { outcomePresentation, severityPresentation } from '../../../service/insightPresentation'

import { useI18n } from '@/shared/i18n'
import { Alert } from '@/shared/ui/alert'
import { Badge } from '@/shared/ui/badge'
import { Button } from '@/shared/ui/button'
import { Input } from '@/shared/ui/input'
import { Select, type SelectOption } from '@/shared/ui/select'
import { Skeleton } from '@/shared/ui/skeleton'

const props = defineProps<{
  configs: readonly InsightConfig[]
  selectedId: number | null
  filters: InsightConfigBrowserFilters
  state: WorkbenchAsyncState
  total: number
  offset: number
  pageSize: number
}>()
const emit = defineEmits<{
  'update:selectedId': [value: number]
  'update:filters': [value: InsightConfigBrowserFilters]
  apply: []
  clear: []
  retry: []
  next: []
  previous: []
}>()

const { t } = useI18n()
const options = (keys: string[], prefix: string): SelectOption<string>[] => [
  { value: '', label: t('insights.sysadmin.filters.all') },
  ...keys.map((value) => ({ value, label: t(`${prefix}.${value}`) })),
]
const statusOptions = options(['active', 'inactive', 'draft'], 'insights.sysadmin.status')
const outcomeOptions: SelectOption<string>[] = [
  { value: '', label: t('insights.sysadmin.filters.all') },
  ...['revenue_gain', 'roas_uplift', 'cost_savings', 'lost_revenue'].map((value) => ({
    value,
    label: t(outcomePresentation(value).labelKey),
  })),
]
const severityOptions: SelectOption<string>[] = [
  { value: '', label: t('insights.sysadmin.filters.all') },
  ...['needs_attention', 'opportunity', 'informational', 'actual', 'other'].map((value) => ({
    value,
    label: t(severityPresentation(value).labelKey),
  })),
]
const platformOptions = options(['all', 'google', 'meta', 'tiktok'], 'insights.sysadmin.platform')

function field<K extends keyof InsightConfigBrowserFilters>(key: K) {
  return computed({
    get: () => props.filters[key],
    set: (value: InsightConfigBrowserFilters[K]) =>
      emit('update:filters', { ...props.filters, [key]: value }),
  })
}

const search = field('search')
const status = field('status')
const platform = field('platform')
const outcome = field('outcome')
const severity = field('severity')
</script>

<template>
  <aside
    class="config-browser grid content-start gap-md min-w-0 p-md border border-border rounded-lg bg-surface"
    data-testid="insight-config-browser"
  >
    <header>
      <div>
        <h2>{{ t('insights.sysadmin.configBrowser.title') }}</h2>
        <span>{{ t('insights.sysadmin.configBrowser.count', { count: total }) }}</span>
      </div>
    </header>

    <form class="config-browser__filters grid gap-sm" @submit.prevent="emit('apply')">
      <Input v-model="search" :label="t('insights.sysadmin.filters.search')" type="search" />
      <Select
        v-model="status"
        :label="t('insights.sysadmin.filters.status')"
        :options="statusOptions"
      />
      <Select
        v-model="platform"
        :label="t('insights.sysadmin.filters.platform')"
        :options="platformOptions"
      />
      <Select
        v-model="outcome"
        :label="t('insights.sysadmin.filters.outcome')"
        :options="outcomeOptions"
      />
      <Select
        v-model="severity"
        :label="t('insights.sysadmin.filters.severity')"
        :options="severityOptions"
      />
      <div class="config-browser__actions flex justify-between gap-xs">
        <Button type="submit" size="sm">{{ t('insights.sysadmin.filters.apply') }}</Button>
        <Button type="button" size="sm" variant="ghost" @click="emit('clear')">
          {{ t('insights.sysadmin.filters.clear') }}
        </Button>
      </div>
    </form>

    <div v-if="state === 'loading'" class="config-browser__list grid gap-sm" aria-busy="true">
      <Skeleton v-for="index in 4" :key="index" height="4rem" />
    </div>
    <Alert v-else-if="state === 'error'" tone="danger">
      {{ t('insights.sysadmin.configBrowser.error') }}
      <Button size="sm" variant="ghost" @click="emit('retry')">
        {{ t('insights.error.retry') }}
      </Button>
    </Alert>
    <p v-else-if="state === 'empty'" class="config-browser__empty p-lg text-muted text-center">
      {{ t('insights.sysadmin.empty.noConfigs') }}
    </p>
    <div v-else class="config-browser__list grid gap-sm">
      <button
        v-for="config in configs"
        :key="config.id ?? config.enum"
        type="button"
        class="config-browser__item flex items-center justify-between gap-sm p-sm border border-border rounded-md bg-surface text-fg text-start"
        :class="{ 'is-selected': config.id === selectedId }"
        @click="config.id !== undefined && emit('update:selectedId', config.id)"
      >
        <span>
          <strong>{{ config.label || config.enum }}</strong>
          <small>{{ config.enum }}</small>
        </span>
        <Badge :tone="config.status === 'active' ? 'success' : 'neutral'" size="sm">
          {{ config.status }}
        </Badge>
      </button>
    </div>

    <footer>
      <Button :disabled="offset === 0" size="sm" variant="ghost" @click="emit('previous')">
        {{ t('insights.sysadmin.pagination.previous') }}
      </Button>
      <Button
        :disabled="offset + pageSize >= total"
        size="sm"
        variant="ghost"
        @click="emit('next')"
      >
        {{ t('insights.sysadmin.pagination.next') }}
      </Button>
    </footer>
  </aside>
</template>

<style scoped>
.config-browser h2 {
  margin: 0;
}
.config-browser p {
  margin: 0;
}
.config-browser header span {
  color: var(--ui-color-muted);
}
.config-browser small {
  color: var(--ui-color-muted);
}
.config-browser footer {
  display: flex;
  justify-content: space-between;
  gap: var(--ui-space-xs);
}
.config-browser .config-browser__item span {
  display: grid;
  min-width: 0;
}

.config-browser__item.is-selected {
  border-color: var(--ui-color-primary);
  background-color: var(--ui-color-primary-subtle);
}
</style>
